import { StyleSheet, Text, View } from "@react-pdf/renderer";

/**
 * The FS Sheet's label/value table.
 *
 * Styling is the table this sheet has always printed (bordered block, bold label
 * column on the left, tinted value column on the right) — carried over verbatim
 * from `PdfStyledTable`, which is what the sheet used before it was given real
 * section labels. What changed is the CONTENT: it takes an explicit
 * `[{ label, value }]` list, so the printed labels are the FS sheet's own wording
 * ("Borrower Trust/Entity Name", "Closing Agent POC Email Address") instead of a
 * title-cased guess at a form field name.
 *
 * `hideEmpty` drops rows with no value. Off by default: on a request sheet a
 * blank row is information — it tells the closing agent what is still
 * outstanding — so suppressing them is the closer's call, not the default.
 */

const BORDER_WIDTH = 1;
const BORDER_COLOR = "#ccc";

const styles = StyleSheet.create({
  container: {
    overflow: "hidden",
    borderRadius: 6,
    border: `${BORDER_WIDTH}px solid ${BORDER_COLOR}`,
  },
  row: {
    flexDirection: "row",
    borderBottom: `${BORDER_WIDTH}px solid ${BORDER_COLOR}`,
    alignItems: "stretch",
  },
  lastRow: {
    borderBottom: "none",
  },
  cellKey: {
    flex: 1,
    padding: 8,
    borderRightWidth: BORDER_WIDTH,
    borderRightColor: BORDER_COLOR,
    borderRightStyle: "solid",
    justifyContent: "center",
  },
  cellKeyText: {
    fontSize: 8,
    fontWeight: "bold",
  },
  cellValue: {
    flex: 2,
    padding: 8,
    backgroundColor: "#f9fcff",
    justifyContent: "center",
  },
  cellValueText: {
    fontSize: 8,
  },
  cellValueEmpty: {
    fontSize: 8,
    color: "#9aa5b1",
  },
});

const hasValue = (v) => v !== null && v !== undefined && v !== "";

/** Rows this table would actually print — lets a caller skip an empty section. */
export const visibleRows = (rows, hideEmpty) =>
  (rows || []).filter(Boolean).filter((r) => !hideEmpty || hasValue(r.value));

const PdfFsFieldTable = ({ rows, hideEmpty = false }) => {
  const list = visibleRows(rows, hideEmpty);
  if (!list.length) return null;

  return (
    <View style={styles.container}>
      {list.map((row, i) => {
        const filled = hasValue(row.value);
        const isLast = i === list.length - 1;
        return (
          <View
            key={row.label}
            style={isLast ? [styles.row, styles.lastRow] : styles.row}
            wrap={false}
          >
            <View style={styles.cellKey}>
              <Text style={styles.cellKeyText}>{row.label}</Text>
            </View>
            <View style={styles.cellValue}>
              <Text style={filled ? styles.cellValueText : styles.cellValueEmpty}>
                {filled ? String(row.value) : "—"}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default PdfFsFieldTable;
